import {
  type Instant,
  type LocalDate,
  type Money,
  formatLocalDate,
  fromApiMoney,
  parseInstant,
  parseLocalDate,
  toApiMoney,
} from '@taler/domain'
import type { Schemas } from '../contract/types.ts'
import type { TalerClient } from '../http/client.ts'
import { type CacheKey, type Invalidations, ledgerKey } from '../http/keys.ts'

export interface Snapshot {
  readonly id: string
  readonly accountId: string
  readonly observedBalance: Money
  readonly observedDate: LocalDate
  /** Absent when the observed balance already matched the recorded one - no correction was needed. */
  readonly correctingEntryId: string | undefined
  /** Set once the correcting entry above was reversed; the books no longer reflect this reconciliation. */
  readonly correctingEntryReversedAt: Instant | undefined
  /** Set when a reconciliation session produced this row, rather than the old single-account flow. */
  readonly reconciliationId: string | undefined
  readonly createdBy: string
  readonly createdAt: Instant
  readonly updatedAt: Instant
}

export interface RecordSnapshotInput {
  readonly accountId: string
  readonly observedBalance: Money
  readonly observedDate: LocalDate
}

export const snapshotKeys = {
  list: (ledgerId: string, accountId: string): CacheKey =>
    ledgerKey(ledgerId, 'snapshots', 'list', accountId),
  /** A write also changes the account's own recorded balance, so a reporting read must be treated as stale too. */
  invalidatedByWrite: (ledgerId: string, accountId: string): Invalidations => [
    ledgerKey(ledgerId, 'snapshots', 'list', accountId),
    ledgerKey(ledgerId, 'reporting'),
  ],
} as const

export interface SnapshotsRepository {
  list(ledgerId: string, accountId: string, signal?: AbortSignal): Promise<readonly Snapshot[]>
  get(ledgerId: string, snapshotId: string, signal?: AbortSignal): Promise<Snapshot>
  create(ledgerId: string, input: RecordSnapshotInput): Promise<Snapshot>
}

export function createSnapshotsRepository(client: TalerClient): SnapshotsRepository {
  const base = (ledgerId: string) => `/api/v1/ledgers/${encodeURIComponent(ledgerId)}/snapshots`
  const one = (ledgerId: string, snapshotId: string) => `${base(ledgerId)}/${encodeURIComponent(snapshotId)}`

  return {
    async list(ledgerId, accountId, signal) {
      const snapshots = await client.call<Schemas['BalanceSnapshotResponse'][]>({
        method: 'GET',
        path: base(ledgerId),
        query: { accountId },
        ...(signal === undefined ? {} : { signal }),
      })
      return snapshots.map(toSnapshot)
    },

    async get(ledgerId, snapshotId, signal) {
      return toSnapshot(
        await client.call<Schemas['BalanceSnapshotResponse']>({
          method: 'GET',
          path: one(ledgerId, snapshotId),
          ...(signal === undefined ? {} : { signal }),
        }),
      )
    },

    async create(ledgerId, input) {
      return toSnapshot(
        await client.call<Schemas['BalanceSnapshotResponse'], Schemas['RecordSnapshotRequest']>({
          method: 'POST',
          path: base(ledgerId),
          body: {
            accountId: input.accountId,
            observedBalance: apiMoney(input.observedBalance),
            observedDate: formatLocalDate(input.observedDate),
          },
        }),
      )
    },
  }
}

/** See `loans.ts`'s identical helper: `@taler/domain`'s currency list is wider than the wire enum. */
function apiMoney(value: Money): Schemas['ApiMoney'] {
  return toApiMoney(value) as Schemas['ApiMoney']
}

function toSnapshot(response: Schemas['BalanceSnapshotResponse']): Snapshot {
  return {
    id: response.id,
    accountId: response.accountId,
    observedBalance: fromApiMoney(response.observedBalance),
    observedDate: parseLocalDate(response.observedDate),
    correctingEntryId: response.correctingEntryId,
    correctingEntryReversedAt:
      response.correctingEntryReversedAt === undefined
        ? undefined
        : parseInstant(response.correctingEntryReversedAt),
    reconciliationId: response.reconciliationId,
    createdBy: response.createdBy,
    createdAt: parseInstant(response.createdAt),
    updatedAt: parseInstant(response.updatedAt),
  }
}
